import { RadioCardProps } from "src/global/ui/Radio/RadioCardGroup.tsx"
import { ColumnTypeOperator } from "src/pages/DataAssets/views/Expectations/Expectation/uiForms/customRenderers/columnType"
import { DataQualityIssue } from "src/global/schemas/expectations/expectation-metadata-data-quality-issues.ts"

export type ExpectationCardProps = RadioCardProps & {
  value: string
  dataQualityIssues?: DataQualityIssue[]
  title: string
}

export type SelectedExpectation = Pick<ExpectationCardProps, "value" | "dataQualityIssues" | "title">

export type DynamicParameterWindowInput = {
  offset: "+/-" | "+" | "-"
  percent: number
  range: number
  strict: boolean
}

export type DynamicParameterWindow = {
  constraint_fn: "mean"
  offset: {
    positive: number
    negative: number
  }
  parameter_name: string
  range: number
  strict: boolean
}

export type ForecastParameterWindow = Record<string, unknown> & {
  constraint_fn: "forecast"
  parameter_name: string
}

export type AnomalyDetectionWindow = DynamicParameterWindow | ForecastParameterWindow

/**
 * Type guard to check if a window is a Dynamic Parameter window
 */
export function isDynamicParameterWindow(
  window: AnomalyDetectionWindow | null | undefined,
): window is DynamicParameterWindow {
  return window?.constraint_fn === "mean"
}

/**
 * Type guard to check if a window is a Forecast Parameter window
 */
export function isForecastParameterWindow(
  window: AnomalyDetectionWindow | null | undefined,
): window is ForecastParameterWindow {
  return window?.constraint_fn === "forecast"
}

type ToBeBetweenOptionsMarkers = {
  isForecastRange?: boolean
  isFixedRange?: boolean
  isDynamicRange?: boolean
}

export type MinMaxFixed = {
  min_value: number
  max_value: number
  strict_min: boolean
  strict_max: boolean
  strict: boolean
}

export type MostlyFixed = {
  mostly: number
  strict: boolean
}

export type MostlyWindow = DynamicParameterWindowInput & {
  mostly: number
}

export type MaybeStrict = {
  strict?: boolean
}

export type ToBeBetween<T> = // Case 1: Has min and max values
  (T extends { min_value: number; max_value: number }
    ? MinMaxFixed
    : // Case 2: Has mostly and percent
      T extends { mostly: number; percent: number }
      ? MostlyWindow
      : // Case 3: Has mostly only
        T extends { mostly: number }
        ? MostlyFixed
        : // Case 4: Has offset
          T extends { offset: string }
          ? DynamicParameterWindowInput
          : // Case 5: Possibly has strict
            T extends { strict?: boolean }
            ? MaybeStrict
            : // Default case: return T as-is
              T) &
    ToBeBetweenOptionsMarkers

export type WindowConfig = DynamicParameterWindowInput | MostlyWindow

export type RowCondition = {
  column: string
  operator: ColumnTypeOperator
  parameter?: string | number
}

export type ExpectationConfig<T> = Record<string, unknown> & {
  to_be_between?: ToBeBetween<T>
  row_condition?: RowCondition
  windows?: AnomalyDetectionWindow[]
}
